import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/** Normalize Indian mobile number to WhatsApp-compatible format (91XXXXXXXXXX) */
const normalizePhone = (phone) => {
  const digits = (phone || '').replace(/[^0-9]/g, '');
  if (digits.length === 10) return '91' + digits;          // bare 10-digit
  if (digits.length === 11 && digits.startsWith('0')) return '91' + digits.slice(1); // 0XXXXXXXXXX
  if (digits.length === 12 && digits.startsWith('91')) return digits; // already has ISD
  return digits; // fallback — use as-is
};

/** Format a date string as readable Indian date */
const fmtDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const generateInvoicePDF = (order) => {
  try {
    const doc = new jsPDF();
    
    // Header — company details
    doc.setFontSize(22);
    doc.setTextColor(217, 119, 6);
    doc.text('HHouse of Breadss', 20, 25);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text('R3/015A, M3M Cosmopolitan', 20, 33);
    doc.text('Sec 66, Gurugram, Haryana', 20, 38);
    doc.text('Phone: +91-9999183418', 20, 43);
    doc.text('Email: hhouseofbreadss@gmail.com', 20, 48);
    
    // Invoice title
    doc.setFontSize(24);
    doc.setTextColor(0);
    doc.text('INVOICE', 150, 25);
    
    // Invoice meta
    doc.setFontSize(10);
    doc.setTextColor(80);
    const orderId = order?.id ? order.id.substring(0, 8).toUpperCase() : 'N/A';
    const orderDate = fmtDate(order?.created_at) || 'N/A';
    const paymentStatus = order?.payment_status?.toUpperCase() || 'PENDING';
    
    doc.text(`Invoice #: INV-${orderId}`, 150, 33);
    doc.text(`Order Date: ${orderDate}`, 150, 38);
    doc.text(`Payment: ${paymentStatus}`, 150, 43);
    
    // Delivery date (scheduled_date)
    const deliveryDate = fmtDate(order?.scheduled_date);
    if (deliveryDate) {
      doc.setTextColor(217, 119, 6);
      doc.setFontSize(10);
      doc.text(`Delivery Date: ${deliveryDate}`, 150, 48);
    }
    
    // Divider
    doc.setDrawColor(217, 119, 6);
    doc.setLineWidth(1);
    doc.line(20, 55, 190, 55);
    
    // Customer details
    doc.setFontSize(12);
    doc.setTextColor(217, 119, 6);
    doc.text('BILL TO:', 20, 65);
    
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text(order?.name || 'Customer', 20, 73);
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text(order?.phone || '', 20, 79);
    
    const address = order?.delivery_type === 'pickup'
      ? 'Store Pickup — R3/015A, M3M Cosmopolitan, Sec 66, Gurugram'
      : (order?.delivery_address || 'N/A');
    const addressLines = doc.splitTextToSize(address, 80);
    doc.text(addressLines, 20, 85);
    
    // Delivery type + date badge (right side)
    const deliveryLabel = order?.delivery_type === 'pickup' ? 'STORE PICKUP' : 'HOME DELIVERY';
    doc.setFontSize(9);
    doc.setTextColor(255);
    doc.setFillColor(217, 119, 6);
    doc.roundedRect(120, 62, 70, 8, 2, 2, 'F');
    doc.text(deliveryLabel, 155, 67.5, { align: 'center' });
    
    if (deliveryDate) {
      doc.setFillColor(254, 243, 199);
      doc.setTextColor(146, 64, 14);
      doc.roundedRect(120, 73, 70, 8, 2, 2, 'F');
      doc.setFontSize(8);
      doc.text(`Delivery: ${deliveryDate}`, 155, 78.5, { align: 'center' });
    }
    
    // Items table
    const items = order?.items || [];
    const tableData = items.map(item => {
      const name = item?.product?.name || item?.name || 'Product';
      const weight = item?.product?.weight || item?.weight || '-';
      const qty = item?.quantity || 0;
      const price = item?.product?.price || item?.price || 0;
      const total = price * qty;
      return [name, weight, qty.toString(), `Rs ${price.toFixed(0)}`, `Rs ${total.toFixed(0)}`];
    });
    
    autoTable(doc, {
      startY: 100,
      head: [['Item', 'Weight', 'Qty', 'Price', 'Total']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [217, 119, 6],
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center'
      },
      styles: {
        fontSize: 9,
        cellPadding: 5,
        textColor: [0, 0, 0]
      },
      columnStyles: {
        0: { cellWidth: 65 },
        1: { cellWidth: 30, halign: 'center' },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 30, halign: 'right' },
        4: { cellWidth: 35, halign: 'right' }
      },
      alternateRowStyles: { fillColor: [255, 248, 240] }
    });
    
    // Summary
    const finalY = doc.lastAutoTable.finalY + 10;
    const subtotal = order?.subtotal || 0;
    const deliveryCharge = order?.delivery_charge || 0;
    const discount = order?.discount || 0;
    const orderTotal = order?.total || (subtotal + deliveryCharge - discount);
    
    doc.setFillColor(255, 248, 240);
    doc.roundedRect(120, finalY - 2, 70, discount > 0 ? 53 : 45, 3, 3, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text('Subtotal:', 125, finalY + 8);
    doc.text(`Rs ${subtotal.toFixed(0)}`, 185, finalY + 8, { align: 'right' });
    
    doc.text('Delivery:', 125, finalY + 16);
    if (deliveryCharge === 0) {
      doc.setTextColor(34, 197, 94);
      doc.text(order?.delivery_type === 'pickup' ? 'PICKUP' : 'FREE', 185, finalY + 16, { align: 'right' });
    } else {
      doc.setTextColor(80);
      doc.text(`Rs ${deliveryCharge.toFixed(0)}`, 185, finalY + 16, { align: 'right' });
    }
    
    if (discount > 0) {
      doc.setTextColor(34, 197, 94);
      doc.text('Discount:', 125, finalY + 24);
      doc.text(`-Rs ${discount.toFixed(0)}`, 185, finalY + 24, { align: 'right' });
    }
    
    const totalLineY = discount > 0 ? finalY + 32 : finalY + 24;
    doc.setDrawColor(217, 119, 6);
    doc.setLineWidth(0.5);
    doc.line(125, totalLineY + 4, 185, totalLineY + 4);
    
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.setFont(undefined, 'bold');
    doc.text('TOTAL:', 125, totalLineY + 12);
    doc.setTextColor(217, 119, 6);
    doc.text(`Rs ${orderTotal.toFixed(0)}`, 185, totalLineY + 12, { align: 'right' });
    
    // Notes
    if (order?.notes) {
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);
      doc.setTextColor(80);
      const notesY = totalLineY + 22;
      doc.text('Notes:', 20, notesY);
      const notesLines = doc.splitTextToSize(order.notes, 90);
      doc.text(notesLines, 20, notesY + 5);
    }
    
    // Footer
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100);
    const footerY = doc.internal.pageSize.height - 25;
    doc.setDrawColor(217, 119, 6);
    doc.setLineWidth(0.3);
    doc.line(20, footerY - 5, 190, footerY - 5);
    doc.text('Thank you for your order!', 105, footerY, { align: 'center' });
    doc.text('All products are 100% eggless and made fresh with love.', 105, footerY + 5, { align: 'center' });
    doc.text('For queries, contact: +91-9999183418 | www.hhouseofbreadss.com', 105, footerY + 10, { align: 'center' });
    
    return doc;
  } catch (error) {
    console.error('Error in generateInvoicePDF:', error);
    throw error;
  }
};

export const downloadInvoice = (order) => {
  try {
    if (!order || !order.id) throw new Error('Invalid order data');
    const doc = generateInvoicePDF(order);
    const orderId = order.id.substring(0, 8).toUpperCase();
    doc.save(`HHouse_Invoice_${orderId}.pdf`);
    return true;
  } catch (error) {
    console.error('Error downloading invoice:', error);
    throw new Error('Failed to generate invoice. Please try again.');
  }
};

export const getInvoiceBlob = (order) => {
  try {
    if (!order || !order.id) throw new Error('Invalid order data');
    const doc = generateInvoicePDF(order);
    return doc.output('blob');
  } catch (error) {
    console.error('Error generating invoice blob:', error);
    throw new Error('Failed to generate invoice.');
  }
};

export const shareInvoiceViaWhatsApp = (order) => {
  try {
    if (!order) throw new Error('Invalid order data');

    const orderId = order.id ? order.id.substring(0, 8).toUpperCase() : 'N/A';
    const phone = normalizePhone(order.phone);
    const deliveryDate = fmtDate(order?.scheduled_date);
    const orderDate = fmtDate(order?.created_at) || 'N/A';
    const deliveryType = order?.delivery_type === 'pickup' ? 'Store Pickup' : 'Home Delivery';
    const deliveryAddress = order?.delivery_type === 'pickup'
      ? 'R3/015A, M3M Cosmopolitan, Sec 66, Gurugram'
      : (order?.delivery_address || 'N/A');

    // Build items list
    const items = order?.items || [];
    const itemLines = items.map(item => {
      const name = item?.product?.name || item?.name || 'Product';
      const qty = item?.quantity || 0;
      const price = item?.product?.price || item?.price || 0;
      return `  • ${name} × ${qty} — ₹${(price * qty).toFixed(0)}`;
    });

    const subtotal = order?.subtotal || 0;
    const deliveryCharge = order?.delivery_charge || 0;
    const discount = order?.discount || 0;
    const orderTotal = order?.total || (subtotal + deliveryCharge - discount);

    const message = [
      `🍞 *HHouse of Breadss*`,
      `📋 *Order Summary — #${orderId}*`,
      ``,
      `👤 ${order?.name || 'Customer'}`,
      `📱 ${order?.phone || 'N/A'}`,
      ``,
      `📦 *Items:*`,
      ...itemLines,
      ``,
      `🚚 *Delivery:* ${deliveryType}`,
      `📍 ${deliveryAddress}`,
      deliveryDate ? `📅 *Delivery Date:* ${deliveryDate}` : `🗓 Order Date: ${orderDate}`,
      ``,
      `💰 *Bill Summary:*`,
      `  Subtotal: ₹${subtotal.toFixed(0)}`,
      deliveryCharge > 0 ? `  Delivery: ₹${deliveryCharge.toFixed(0)}` : `  Delivery: ${deliveryType === 'Store Pickup' ? 'Pickup' : 'Free'}`,
      discount > 0 ? `  Discount: -₹${discount.toFixed(0)}` : null,
      `  *Total: ₹${orderTotal.toFixed(0)}*`,
      ``,
      `Payment Status: ${(order?.payment_status || 'Pending').toUpperCase()}`,
      order?.payment_link ? `\n💳 Pay here: ${order.payment_link}` : null,
      ``,
      `Thank you for ordering with us! 🙏`,
    ].filter(line => line !== null).join('\n');

    const whatsappURL = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappURL, '_blank');
    return true;
  } catch (error) {
    console.error('Error sharing invoice:', error);
    throw new Error('Failed to share invoice. Please try again.');
  }
};

